import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type VoiceLanguageCode = "fr" | "en" | "es" | "pt";

const STORAGE_PREFIX = "ava.lang.";

function pickFromList(list: readonly string[] | undefined, supported: readonly VoiceLanguageCode[]) {
  if (!list) return undefined;
  for (const raw of list) {
    const code = raw?.split("-")[0]?.toLowerCase();
    if (code && supported.includes(code as VoiceLanguageCode)) return code as VoiceLanguageCode;
  }
  return undefined;
}

/**
 * Resolve voice-agent UI language with priority:
 *   1. user-set localStorage override (set via setLanguage)
 *   2. app i18next current language (if supported)
 *   3. navigator.languages[] first supported match
 *   4. fallback (default "fr")
 */
export function useResolvedVoiceLanguage(
  projectId: string,
  supported: readonly VoiceLanguageCode[] = ["fr", "en", "es", "pt"],
  fallback: VoiceLanguageCode = "fr",
) {
  const { i18n } = useTranslation();
  const storageKey = `${STORAGE_PREFIX}${projectId}`;

  const resolve = (): VoiceLanguageCode => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && supported.includes(stored as VoiceLanguageCode)) return stored as VoiceLanguageCode;
    } catch { /* noop */ }
    const fromI18n = i18n.language?.split("-")[0]?.toLowerCase();
    if (fromI18n && supported.includes(fromI18n as VoiceLanguageCode)) return fromI18n as VoiceLanguageCode;
    if (typeof navigator !== "undefined") {
      const fromNav = pickFromList(navigator.languages ?? [navigator.language], supported);
      if (fromNav) return fromNav;
    }
    return fallback;
  };

  const [language, setLanguageState] = useState<VoiceLanguageCode>(resolve);

  useEffect(() => {
    setLanguageState(resolve());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, projectId]);

  const setLanguage = (lang: VoiceLanguageCode) => {
    try { localStorage.setItem(storageKey, lang); } catch { /* noop */ }
    setLanguageState(lang);
  };

  return { language, setLanguage } as const;
}